import { PrayerAuthoringSession, prayerEditorIsCurrent } from './prayer-authoring';
import { getLocalContentEntity, listPrayerAttempts } from './content-lifecycle/store';
import type { ContentLifecycleAuthExpectation } from './content-lifecycle/store';
import { completePrayerDocument } from './content-lifecycle/contracts';
import { parseScriptureAnchor } from './scripture-anchor';
import { mergeRecoveredVoiceText } from './audio-recovery';
import { holdUpdate } from './update-safety';
import { getSupabase } from './supabase';
import { useAdiaStore } from './store';
import type { Prayer, ScriptureAnchor } from '@/types/prayer';
import type { PrayerAttemptV1, PrayerIntent, PrayerLocalCommitResult } from '@/types/prayer-command';
import type { RecoveredAudioCommitClaim } from '@/types/audio-recovery';
import type { CaptureDurableNotice } from '@/hooks/useSpeechToText';

export interface ScriptureDraftSnapshot {
  text: string;
  revision: number;
  loading: 'loading' | 'ready' | 'failed' | 'multiple';
  resolution: 'none' | 'saved' | 'unknown' | 'changed';
  operation: 'save' | 'discard' | null;
  saveError: boolean;
  saveReopen: 'words' | 'recording' | null;
  discardError: boolean;
  heldConflict: { kind: 'saved' | 'unknown'; ids: string[] } | null;
  capture: 'idle' | 'stopping' | 'failed';
  noWords: boolean;
  audioRevision: number;
}
type Words = { text: string; queueId?: string };
type Settlement = {
  auth: ContentLifecycleAuthExpectation;
  reviewed: PrayerAttemptV1;
  executed: PrayerAttemptV1;
  result: Extract<PrayerLocalCommitResult, { kind: 'committed' }> | null;
};
const drafts = new Map<string, ScriptureDraft>();
const clone = <T,>(value: T): T => structuredClone(value);
const equal = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
/** Full normalized frozen identity; never a reference-only or chapter key. */
export function scriptureDraftAnchorKey(value: unknown): string | null {
  const parsed = parseScriptureAnchor(value);
  return parsed ? JSON.stringify(parsed) : null;
}
const keyFor = (auth: ContentLifecycleAuthExpectation, anchorKey: string) =>
  `${auth.ownerId}:${auth.authEpoch}:${anchorKey}`;
let stopWatching: (() => void) | null = null;
function retire(ownerId?: string | null) {
  for (const [key, draft] of drafts) {
    if ((ownerId !== undefined && ownerId !== draft.auth.ownerId) || !prayerEditorIsCurrent(draft.auth)) {
      draft.dispose(); drafts.delete(key);
    }
  }
  if (!drafts.size) { stopWatching?.(); stopWatching = null; }
}
function watch() {
  if (stopWatching) return;
  const unsubscribe = useAdiaStore.subscribe(() => retire());
  const subscription = getSupabase()?.auth.onAuthStateChange((_event, session) => {
    retire(session?.user.id ?? null); queueMicrotask(() => retire());
  }).data.subscription;
  stopWatching = () => { unsubscribe(); subscription?.unsubscribe(); };
}

/** One current-document, captured-session prayer draft. Existing transactions
 * remain the only durable authoring authority; this record never writes storage. */
export class ScriptureDraft {
  readonly auth: ContentLifecycleAuthExpectation;
  readonly voiceTarget = `pray-scripture-${crypto.randomUUID()}`;
  readonly anchorKey: string;
  anchor: ScriptureAnchor;
  session: PrayerAuthoringSession;
  candidate: Prayer;
  readonly knownIds = new Set<string>();
  readonly activeIds = new Set<string>();
  targetRegistration = 0;
  activeCaptureToken: string | null = null;
  readonly receivedIds = new Set<string>();
  readonly excludedIds = new Set<string>();
  readonly heldWords = new Map<string, string>();
  readonly captureTokens = new Set<string>();
  readonly durableTokens = new Set<string>();
  private pendingWords: Words[] = [];
  private listeners = new Set<() => void>();
  private loadPromise: Promise<void> | null = null;
  private initialized = false;
  private candidateClockCaptured = false;
  private disposed = false;
  private releaseHold: (() => void) | null = null;
  private binding: { attempt: PrayerAttemptV1; revision: number; ids: string[] } | null = null;
  private pendingSettlement: Settlement | null = null;
  private snapshot: ScriptureDraftSnapshot = { text: '', revision: 0, loading: 'loading', resolution: 'none',
    operation: null, saveError: false, saveReopen: null, discardError: false, heldConflict: null,
    capture: 'idle', noWords: false, audioRevision: 0 };
  constructor(auth: ContentLifecycleAuthExpectation, anchor: ScriptureAnchor) {
    this.auth = auth;
    const key = scriptureDraftAnchorKey(anchor);
    if (!key) throw new Error('invalid-scripture-draft-anchor');
    this.anchorKey = key; this.anchor = clone(anchor);
    this.session = new PrayerAuthoringSession(auth); this.candidate = this.newCandidate();
  }
  private newCandidate(): Prayer {
    const now = new Date().toISOString();
    return { id: crypto.randomUUID(), rawText: '', parsed: null, status: 'Open', progressHistory: [],
      createdAt: now, updatedAt: now, scriptureAnchor: clone(this.anchor) };
  }
  readonly getSnapshot = () => this.snapshot;
  readonly subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  isCurrent() { return !this.disposed && prayerEditorIsCurrent(this.auth) && drafts.get(keyFor(this.auth, this.anchorKey)) === this; }
  matchesAnchor(anchor: unknown) { return scriptureDraftAnchorKey(anchor) === this.anchorKey; }
  private syncHold() {
    const needed = this.isCurrent() && Boolean(this.snapshot.text || this.pendingWords.length || this.snapshot.operation || this.snapshot.capture === 'stopping');
    if (needed && !this.releaseHold) this.releaseHold = holdUpdate('pray-scripture-draft');
    else if (!needed && this.releaseHold) { const release = this.releaseHold; this.releaseHold = null; release(); }
  }
  dispose() { this.disposed = true; this.syncHold(); for (const listener of this.listeners) listener(); }
  patch(patch: Partial<ScriptureDraftSnapshot>) {
    if (!this.isCurrent()) return;
    this.snapshot = { ...this.snapshot, ...patch }; this.syncHold();
    for (const listener of this.listeners) listener();
  }
  setText(text: string) {
    if (!this.isCurrent() || this.snapshot.loading !== 'ready' || this.snapshot.operation) return;
    this.patch({ text, revision: this.snapshot.revision + 1, saveError: false, saveReopen: null,
      discardError: false, heldConflict: null, noWords: false });
  }
  rememberSubmittedAttempt(session: PrayerAuthoringSession, revision: number,
    intent: Extract<PrayerIntent, { kind: 'create' }>, claims: readonly RecoveredAudioCommitClaim[], ids: Iterable<string> = claims.map(claim => claim.queueItemId)) {
    const attempt = session.attempt;
    if (!this.isCurrent() || this.session !== session || !attempt || attempt.ownerId !== this.auth.ownerId ||
      attempt.prayerId !== this.candidate.id || !equal(attempt.intent, intent) || !equal(attempt.recoveredAudio, claims)) return;
    this.binding = { attempt: clone(attempt), revision, ids: [...ids] };
  }
  load(refresh = false) {
    if (this.loadPromise || (!refresh && this.snapshot.loading === 'ready') || !this.isCurrent()) return this.loadPromise;
    this.patch({ loading: 'loading' });
    this.loadPromise = (async () => {
      const attempts = await listPrayerAttempts(this.auth.ownerId);
      if (!this.isCurrent()) return;
      const matching = attempts.filter(a => a.intent.kind === 'create' && this.matchesAnchor(a.intent.prayer.scriptureAnchor));
      const previous = this.session.attempt;
      if (previous) {
        const current = attempts.find(a => a.commandId === previous.commandId);
        if (current && !equal(previous, current)) this.patch({ resolution: 'changed' });
        else if (!current) {
          const entity = await getLocalContentEntity(this.auth.ownerId, 'prayer', previous.prayerId);
          if (!this.isCurrent()) return;
          const opMatches = entity?.pending?.opId === previous.commandId || entity?.installedEnvelope?.opId === previous.commandId;
          const saved = previous.intent.kind === 'create' && equal(entity?.visibleDocument, completePrayerDocument(previous.intent.prayer));
          if (opMatches && saved && entity && !entity.pendingDeleteAt && !entity.heldReason && !entity.quarantineReason) this.settleResolved(previous, true, []);
          else this.patch({ resolution: 'unknown' });
        } else this.patch({ resolution: 'none' });
        if (matching.some(a => a.commandId !== previous.commandId)) this.patch({ loading: 'multiple' });
      } else if (matching.length) {
        if (matching.length > 1 || this.initialized || this.snapshot.text) this.patch({ loading: 'multiple' });
        else {
          const attempt = matching[0];
          if (attempt.intent.kind === 'create') {
            this.session.attempt = attempt; this.candidate = clone(attempt.intent.prayer);
            this.candidateClockCaptured = true;
            this.anchor = clone(attempt.intent.prayer.scriptureAnchor!);
            this.patch({ text: attempt.intent.prayer.rawText, revision: this.snapshot.revision + 1 });
            this.binding = { attempt: clone(attempt), revision: this.snapshot.revision, ids: attempt.recoveredAudio.map(claim => claim.queueItemId) };
            for (const claim of attempt.recoveredAudio) {
              this.knownIds.add(claim.queueItemId); this.activeIds.add(claim.queueItemId); this.receivedIds.add(claim.queueItemId);
              if ('expectedTranscribedText' in claim) this.heldWords.set(claim.queueItemId, claim.expectedTranscribedText);
            }
          }
        }
      }
      this.initialized = true;
      if (this.snapshot.loading !== 'multiple') this.patch({ loading: 'ready' });
      this.flushWords();
    })().catch(() => this.patch({ loading: 'failed' })).finally(() => { this.loadPromise = null; });
    return this.loadPromise;
  }
  /** Constant-time custody observation. The hook reads the authoritative row
   * and checks its full anchor before using words or attempting a save. */
  captureDurable(notice: CaptureDurableNotice) {
    if (!this.isCurrent() || notice.ownerId !== this.auth.ownerId || notice.authEpoch !== this.auth.authEpoch ||
      !notice.captureToken || !this.captureTokens.has(notice.captureToken) || this.durableTokens.has(notice.captureToken) || notice.recoveryDestination) return;
    this.knownIds.add(notice.queueId); this.activeIds.add(notice.queueId); this.durableTokens.add(notice.captureToken);
    this.patch({ audioRevision: this.snapshot.audioRevision + 1, ...(notice.captureToken === this.activeCaptureToken ? { capture: 'idle' as const } : {}) });
  }
  receive(text: string, queueId?: string) {
    if (!this.isCurrent() || !text.trim() || (queueId && (this.receivedIds.has(queueId) || this.excludedIds.has(queueId)))) return;
    if (queueId && this.pendingWords.some(w => w.queueId === queueId)) return;
    const words = { text, queueId };
    if (this.snapshot.operation || this.snapshot.loading !== 'ready') { this.pendingWords.push(words); this.syncHold(); return; }
    this.applyWords(words);
  }
  private applyWords(words: Words) {
    if (words.queueId) {
      if (this.receivedIds.has(words.queueId) || this.excludedIds.has(words.queueId)) return;
      this.knownIds.add(words.queueId); this.activeIds.add(words.queueId); this.receivedIds.add(words.queueId); this.heldWords.set(words.queueId, words.text);
    }
    this.patch({ text: mergeRecoveredVoiceText(this.snapshot.text, words.text), revision: this.snapshot.revision + 1, noWords: false });
  }
  private flushWords() {
    if (!this.isCurrent() || this.snapshot.loading !== 'ready' || this.snapshot.operation) return;
    while (this.pendingWords.length) { this.applyWords(this.pendingWords[0]); this.pendingWords.shift(); }
    this.syncHold();
  }
  getRecoveryText() { return this.pendingWords.reduce((text, words) => mergeRecoveredVoiceText(text, words.text), this.snapshot.text); }
  begin(operation: 'save' | 'discard') {
    if (!this.isCurrent() || this.snapshot.loading !== 'ready' || this.snapshot.operation) return null;
    // The constructor's candidate is ephemeral. Freeze its authored clock on
    // the first Save, even if that attempt fails before durable registration.
    // Loaded/already recorded attempts and Discard never receive a new clock.
    if (operation === 'save' && !this.candidateClockCaptured && !this.session.attempt && !this.binding) {
      const now = new Date().toISOString();
      this.candidate = { ...this.candidate, createdAt: now, updatedAt: now };
      this.candidateClockCaptured = true;
    }
    const captured = { revision: this.snapshot.revision, text: this.snapshot.text, session: this.session,
      candidate: clone(this.candidate), ids: new Set([...this.activeIds].filter(id => !this.excludedIds.has(id))), heldWords: new Map([...this.heldWords].filter(([id]) => !this.excludedIds.has(id))) };
    this.patch({ operation, saveError: false, saveReopen: null, discardError: false, heldConflict: null });
    return captured;
  }
  private resetSession(excluded: Iterable<string>, retired: Iterable<string> = excluded) {
    for (const id of excluded) this.excludedIds.add(id);
    for (const id of retired) { this.activeIds.delete(id); this.heldWords.delete(id); }
    this.binding = null; this.session = new PrayerAuthoringSession(this.auth); this.candidate = this.newCandidate();
    this.candidateClockCaptured = false;
  }
  finish(revision: number, clear: boolean, excluded: Iterable<string> = [], saved = false, retired: Iterable<string> = excluded) {
    if (!this.isCurrent()) return;
    if (clear && revision === this.snapshot.revision) {
      this.resetSession(excluded, retired); this.patch({ text: '', revision: revision + 1, resolution: 'none' });
    }
    this.patch({ operation: null });
    const settlement = this.pendingSettlement; this.pendingSettlement = null;
    if (settlement) this.settle(settlement);
    this.flushWords();
    if (saved && this.snapshot.text) this.patch({ resolution: 'saved' });
  }
  settle(settlement: Settlement) {
    if (!this.isCurrent() || settlement.auth.ownerId !== this.auth.ownerId || settlement.auth.authEpoch !== this.auth.authEpoch ||
      !this.binding || !equal(this.binding.attempt, settlement.reviewed)) return;
    if (this.snapshot.operation) { this.pendingSettlement = settlement; return; }
    const { executed, result } = settlement;
    if (executed.intent.kind !== 'create' || !equal(executed, settlement.reviewed) || executed.prayerId !== this.candidate.id ||
      !this.matchesAnchor(executed.intent.prayer.scriptureAnchor)) return;
    if (result && (result.ownerId !== this.auth.ownerId || result.authEpoch !== this.auth.authEpoch || result.opId !== executed.commandId ||
      !equal(completePrayerDocument(result.prayer), completePrayerDocument(executed.intent.prayer)))) return;
    this.settleResolved(settlement.reviewed, result !== null, result?.consumedAudio.map(c => c.queueItemId) ?? []);
  }
  private settleResolved(previous: PrayerAttemptV1, saved: boolean, consumedIds: string[]) {
    if (!this.binding || !equal(this.binding.attempt, previous)) { this.patch({ resolution: 'unknown' }); return; }
    const unchanged = this.snapshot.revision === this.binding.revision;
    const submittedIds = this.binding.ids;
    this.resetSession(submittedIds.filter(id => !consumedIds.includes(id)), submittedIds);
    this.patch({ ...(unchanged ? { text: '', revision: this.snapshot.revision + 1 } : {}),
      resolution: saved && !unchanged ? 'saved' : 'none', saveError: false, discardError: false });
    this.flushWords();
  }
  chooseSuccessor() {
    if (!this.isCurrent() || this.snapshot.operation || this.snapshot.resolution !== 'saved') return false;
    this.patch({ resolution: 'none' }); return true;
  }
  useRecordingWords(text: string, id: string) {
    if (!this.isCurrent() || this.snapshot.operation || this.snapshot.loading !== 'ready' || !this.excludedIds.has(id)) return;
    this.excludedIds.delete(id); this.receivedIds.delete(id); this.receive(text, id);
  }
  releaseConflictingIds(ids: string[]) {
    if (!this.isCurrent() || this.snapshot.operation) return;
    for (const id of ids) { this.excludedIds.add(id); this.activeIds.delete(id); this.heldWords.delete(id); }
    this.patch({ heldConflict: null });
  }
}
export function prayScriptureDraft(auth: ContentLifecycleAuthExpectation, anchor: ScriptureAnchor) {
  retire();
  const key = keyFor(auth, scriptureDraftAnchorKey(anchor) ?? 'invalid');
  let draft = drafts.get(key);
  if (!draft) {
    draft = new ScriptureDraft(auth, anchor);
    if (prayerEditorIsCurrent(auth)) { drafts.set(key, draft); watch(); }
  }
  return draft;
}
/** Observation only; Settings' successful transaction cannot fail here. */
export function settlePrayScriptureDraft(settlement: Settlement) {
  if (settlement.reviewed.intent.kind !== 'create') return;
  try {
    const anchor = scriptureDraftAnchorKey(settlement.reviewed.intent.prayer.scriptureAnchor);
    if (anchor) drafts.get(keyFor(settlement.auth, anchor))?.settle(settlement);
  } catch { /* retain words and reconcile on reopen */ }
}
